import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Check } from "lucide-react";

export default function TrcaEpcDetail() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: d }, { data: s }] = await Promise.all([
          api.get(`/admin/trca-epc/submissions/${id}`),
          api.get(`/trca-epc/schema`),
        ]);
        setDoc(d); setSchema(s);
      } catch (ex) {
        toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
      } finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <div className="p-10 text-sm text-ink-muted uppercase tracking-wider">Loading…</div>;
  if (!doc || !schema) return <div className="p-10 text-sm text-ink-muted">Not found.</div>;

  return (
    <div className="px-6 py-6 space-y-6" data-testid="trca-epc-admin-detail">
      <Link to="/admin/trca-epc" className="text-xs text-brand inline-flex items-center gap-1 hover:underline">
        <ArrowLeft className="w-3 h-3" /> Back to TRCA EPC submissions
      </Link>

      {/* Header */}
      <div className="card-flat p-6">
        <div className="text-[11px] uppercase tracking-[0.25em] text-ink-muted">User</div>
        <div className="font-display font-bold text-2xl mt-1">{doc.user_name || "—"}</div>
        <div className="text-sm text-ink-muted">{doc.user_email}</div>
        <div className="mt-4 flex items-baseline gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-ink-muted">Total Score</div>
            <div className="font-num font-bold text-4xl">{doc.total_score}<span className="text-ink-muted text-lg">/100</span></div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-ink-muted">Tier</div>
            <div className="font-display font-bold text-lg mt-1">{doc.tier_label || "—"}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-ink-muted">Completed</div>
            <div className="font-num text-sm mt-1">{(doc.completed_at || "").slice(0, 19)}</div>
          </div>
        </div>
      </div>

      {/* Category breakdown (percent) */}
      <div className="card-flat p-6">
        <div className="stat-label mb-4">Category breakdown</div>
        <div className="space-y-3">
          {(doc.category_breakdown || []).map((c) => {
            const pct = c.pct ?? Math.round((c.score / c.max_score) * 100);
            return (
              <div key={c.id} className="space-y-1" data-testid={`trca-epc-detail-cat-${c.id}`}>
                <div className="flex items-baseline justify-between text-sm">
                  <span className="font-medium">{c.label}</span>
                  <span className="font-num text-ink-muted">{pct}% · {c.score} / {c.max_score}</span>
                </div>
                <div className="h-2 bg-secondary rounded-sm overflow-hidden">
                  <div className="h-full bg-brand transition-all" style={{ width: `${Math.min(100, pct)}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Per-question selected options */}
      <div className="space-y-4">
        {schema.categories.map((cat) => (
          <div key={cat.id} className="card-flat p-6">
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">{cat.label}</div>
            <div className="space-y-5 mt-4">
              {cat.questions.map((q) => {
                const selected = doc.answers?.[q.code] || [];
                return (
                  <div key={q.code} data-testid={`trca-epc-detail-q-${q.code}`}>
                    <div className="flex items-baseline justify-between mb-2">
                      <div>
                        <span className="text-[11px] font-num text-ink-muted">{q.code}</span>
                        <span className="ml-2 font-display font-semibold text-sm">{q.label}</span>
                      </div>
                      <span className="text-xs font-num text-ink-muted">{selected.length} of {q.options.length} · {(selected.reduce((a, i) => a + Number(q.option_scores?.[i] ?? 0.8), 0)).toFixed(1)} pts</span>
                    </div>
                    <ul className="space-y-1.5">
                      {q.options.map((opt, i) => {
                        const isSel = selected.includes(i);
                        return (
                          <li key={i} className={`flex items-start gap-2 text-[13px] ${isSel ? "text-ink" : "text-ink-muted line-through opacity-60"}`}>
                            <span className={`shrink-0 w-4 h-4 mt-0.5 border rounded-sm flex items-center justify-center ${isSel ? "bg-good border-good text-white" : "border-ink-line"}`}>
                              {isSel && <Check className="w-2.5 h-2.5" />}
                            </span>
                            <span>{opt}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
